import { useEffect, useState } from "react";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
	FeedbackService,
	type FeedbackItemWithUser,
	type FeedbackStatus,
} from "@/lib/feedback/feedback.service";
import { toast } from "sonner";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

/**
 * Feedback Management Page for Platform Managers
 *
 * Features:
 * - View all user feedback submissions
 * - Filter and search feedback
 * - Update feedback status
 * - View detailed feedback information
 * - Sort by date, status, category
 */
export default function FeedbackManagementPage() {
	const { t, i18n } = useTranslation();
	const [feedback, setFeedback] = useState<FeedbackItemWithUser[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedFeedback, setSelectedFeedback] =
		useState<FeedbackItemWithUser | null>(null);
	const [isDetailOpen, setIsDetailOpen] = useState(false);

	// Get the appropriate date-fns locale based on current language
	const dateLocale = i18n.language === "de" ? de : enUS;

	// Fetch feedback on mount
	useEffect(() => {
		loadFeedback();
	}, []);

	const loadFeedback = async () => {
		try {
			setLoading(true);
			const feedbackService = FeedbackService.getInstance();
			const data = await feedbackService.getAllFeedback();
			setFeedback(data);
		} catch (error) {
			console.error("Failed to load feedback:", error);
			toast.error(t("feedback.management.loadFailed"), {
				description: t("feedback.errorMessage"),
			});
		} finally {
			setLoading(false);
		}
	};

	const handleStatusUpdate = async (
		feedbackId: string,
		newStatus: FeedbackStatus,
	) => {
		try {
			const feedbackService = FeedbackService.getInstance();
			await feedbackService.updateFeedbackStatus(feedbackId, newStatus);

			// Update local state
			setFeedback((prev) =>
				prev.map((item) =>
					item.id === feedbackId ? { ...item, status: newStatus } : item,
				),
			);

			toast.success(t("feedback.management.statusUpdated"));
		} catch (error) {
			console.error("Failed to update status:", error);
			toast.error(t("feedback.management.statusUpdateFailed"), {
				description: t("feedback.management.tryAgain"),
			});
		}
	};

	const handleRowClick = (item: FeedbackItemWithUser) => {
		setSelectedFeedback(item);
		setIsDetailOpen(true);
	};

	// Get status badge variant
	const getStatusVariant = (
		status: FeedbackStatus,
	): "default" | "secondary" | "destructive" | "outline" => {
		switch (status) {
			case "NEW":
				return "default";
			case "REVIEWED":
				return "secondary";
			case "IN_PROGRESS":
				return "outline";
			case "RESOLVED":
				return "secondary";
			case "WONT_FIX":
				return "destructive";
			default:
				return "default";
		}
	};

	// Get category badge color
	const getCategoryColor = (category: string): string => {
		switch (category) {
			case "BUG":
				return "bg-red-100 text-red-800 hover:bg-red-100";
			case "FEATURE_REQUEST":
				return "bg-blue-100 text-blue-800 hover:bg-blue-100";
			case "IMPROVEMENT":
				return "bg-purple-100 text-purple-800 hover:bg-purple-100";
			case "UX":
				return "bg-pink-100 text-pink-800 hover:bg-pink-100";
			case "PERFORMANCE":
				return "bg-orange-100 text-orange-800 hover:bg-orange-100";
			default:
				return "bg-gray-100 text-gray-800 hover:bg-gray-100";
		}
	};

	const columns: DataTableColumn<FeedbackItemWithUser>[] = [
		{
			key: "createdAt",
			label: "Date",
			sortable: true,
			accessor: (item) => new Date(item.createdAt),
			render: (item) => (
				<span className="text-sm">
					{formatDistanceToNow(new Date(item.createdAt), {
						addSuffix: true,
						locale: dateLocale,
					})}
				</span>
			),
		},
		{
			key: "category",
			label: "Category",
			sortable: true,
			render: (item) => (
				<Badge className={getCategoryColor(item.category)} variant="outline">
					{FeedbackService.getCategoryIcon(item.category)}{" "}
					{FeedbackService.getCategoryDisplayName(item.category)}
				</Badge>
			),
		},
		{
			key: "title",
			label: "Title",
			sortable: true,
			render: (item) => (
				<div className="max-w-md">
					<button
						type="button"
						onClick={() => handleRowClick(item)}
						className="text-left hover:underline font-medium truncate block w-full"
					>
						{item.title}
					</button>
				</div>
			),
		},
		{
			key: "user",
			label: "User",
			sortable: true,
			accessor: (item) => item.user.email,
			render: (item) => (
				<div className="text-sm">
					<div className="font-medium">{item.user.name || "Unknown"}</div>
					<div className="text-muted-foreground">{item.user.email}</div>
				</div>
			),
		},
		{
			key: "status",
			label: "Status",
			sortable: true,
			render: (item) => (
				<Select
					value={item.status}
					onValueChange={(value) =>
						handleStatusUpdate(item.id, value as FeedbackStatus)
					}
				>
					<SelectTrigger className="w-[150px]">
						<SelectValue>
							<Badge variant={getStatusVariant(item.status)}>
								{FeedbackService.getStatusDisplayName(item.status)}
							</Badge>
						</SelectValue>
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="NEW">
							{t("feedback.management.status.NEW")}
						</SelectItem>
						<SelectItem value="REVIEWED">
							{t("feedback.management.status.REVIEWED")}
						</SelectItem>
						<SelectItem value="IN_PROGRESS">
							{t("feedback.management.status.IN_PROGRESS")}
						</SelectItem>
						<SelectItem value="RESOLVED">
							{t("feedback.management.status.RESOLVED")}
						</SelectItem>
						<SelectItem value="WONT_FIX">
							{t("feedback.management.status.WONT_FIX")}
						</SelectItem>
					</SelectContent>
				</Select>
			),
		},
	];

	return (
		<div className="container mx-auto py-8 px-4">
			<DataTable
				data={feedback}
				columns={columns}
				title={t("feedback.management.title")}
				description={t("platformManager.userManagement.description")}
				searchable
				searchPlaceholder="Search feedback..."
				loading={loading}
				defaultSortField="createdAt"
				defaultSortOrder="desc"
				emptyMessage="No feedback submissions yet"
			/>

			{/* Detail Dialog */}
			<Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
				<DialogContent className="w-[90vw] max-w-[90vw] sm:max-w-[90vw] max-h-[90vh] overflow-y-auto">
					{selectedFeedback && (
						<>
							<DialogHeader>
								<DialogTitle className="flex items-center gap-2">
									<Badge
										className={getCategoryColor(selectedFeedback.category)}
										variant="outline"
									>
										{FeedbackService.getCategoryIcon(selectedFeedback.category)}{" "}
										{FeedbackService.getCategoryDisplayName(
											selectedFeedback.category,
										)}
									</Badge>
									<Badge variant={getStatusVariant(selectedFeedback.status)}>
										{FeedbackService.getStatusDisplayName(
											selectedFeedback.status,
										)}
									</Badge>
								</DialogTitle>
								<DialogDescription>
									Submitted{" "}
									{formatDistanceToNow(new Date(selectedFeedback.createdAt), {
										addSuffix: true,
										locale: dateLocale,
									})}
								</DialogDescription>
							</DialogHeader>

							<div className="space-y-6 mt-4">
								{/* Title */}
								<div>
									<h3 className="text-sm font-medium text-muted-foreground mb-1">
										Title
									</h3>
									<p className="text-lg font-semibold">
										{selectedFeedback.title}
									</p>
								</div>

								{/* Description */}
								<div>
									<h3 className="text-sm font-medium text-muted-foreground mb-1">
										Description
									</h3>
									<p className="text-sm whitespace-pre-wrap">
										{selectedFeedback.description}
									</p>
								</div>

								{/* User Info */}
								<div>
									<h3 className="text-sm font-medium text-muted-foreground mb-1">
										User
									</h3>
									<div className="text-sm">
										<div className="font-medium">
											{selectedFeedback.user.name || "Unknown"}
										</div>
										<div className="text-muted-foreground">
											{selectedFeedback.user.email}
										</div>
									</div>
								</div>

								{/* Current Page */}
								{selectedFeedback.currentPage && (
									<div>
										<h3 className="text-sm font-medium text-muted-foreground mb-1">
											Page
										</h3>
										<code className="text-sm bg-muted px-2 py-1 rounded">
											{selectedFeedback.currentPage}
										</code>
									</div>
								)}

								{/* User Agent */}
								{selectedFeedback.userAgent && (
									<div>
										<h3 className="text-sm font-medium text-muted-foreground mb-1">
											Browser
										</h3>
										<p className="text-xs text-muted-foreground font-mono">
											{selectedFeedback.userAgent}
										</p>
									</div>
								)}

								{/* Metadata */}
								{selectedFeedback.metadata && (
									<div>
										<h3 className="text-sm font-medium text-muted-foreground mb-1">
											Additional Info
										</h3>
										<pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
											{JSON.stringify(selectedFeedback.metadata, null, 2)}
										</pre>
									</div>
								)}

								{/* Status Update */}
								<div>
									<h3 className="text-sm font-medium text-muted-foreground mb-2">
										Update Status
									</h3>
									<Select
										value={selectedFeedback.status}
										onValueChange={(value) => {
											handleStatusUpdate(
												selectedFeedback.id,
												value as FeedbackStatus,
											);
											setSelectedFeedback({
												...selectedFeedback,
												status: value as FeedbackStatus,
											});
										}}
									>
										<SelectTrigger className="w-full">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="NEW">
												{t("feedback.management.status.NEW")}
											</SelectItem>
											<SelectItem value="REVIEWED">
												{t("feedback.management.status.REVIEWED")}
											</SelectItem>
											<SelectItem value="IN_PROGRESS">
												{t("feedback.management.status.IN_PROGRESS")}
											</SelectItem>
											<SelectItem value="RESOLVED">
												{t("feedback.management.status.RESOLVED")}
											</SelectItem>
											<SelectItem value="WONT_FIX">
												{t("feedback.management.status.WONT_FIX")}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>

							<div className="flex justify-end mt-6">
								<Button onClick={() => setIsDetailOpen(false)}>
									{t("common.close")}
								</Button>
							</div>
						</>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
